import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import useOnCloseWithConfirmation from '@/hooks/useOnCloseWithConfirmation';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

type FormDataT = {
  target_url: string;
  min_likes: string;
  fetch_posts_since: string;
  results_limit: string;
  savable_posts_count: string;
};

interface Props {
  onClose: () => void;
}

const Form: FC<Props> = ({ onClose }) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    handleSubmit,
    setError,
    control,
    clearErrors,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      target_url: '',
      min_likes: '',
      fetch_posts_since: '',
      results_limit: '1000',
      savable_posts_count: '50',
    },
  });

  const onCancel = useOnCloseWithConfirmation(onClose, isDirty);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const { id } = await api.post<typeof data, { id: number }>(
          '/v1/tools/social-posts-imports',
          data,
        );

        await queryClient.invalidateQueries(['social_posts_imports']);

        navigate(id.toString());
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true, true);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel heading="New Search" onClose={onCancel}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                control={control}
                label="Source URL"
                inputType="url"
                placeholder="Add FB Page or IG Profile URL"
                name="target_url"
              />
              <FormField<FormDataT>
                control={control}
                label="Min. Likes"
                placeholder="Add min. likes"
                name="min_likes"
              />
              <FormField<FormDataT>
                control={control}
                label="Posts Since"
                type="datepicker"
                name="fetch_posts_since"
              />
              <FormField<FormDataT>
                control={control}
                label="Scrape last X posts"
                placeholder="Add number"
                name="results_limit"
              />
              <FormField<FormDataT>
                control={control}
                label="Save top X posts"
                placeholder="Add number"
                name="savable_posts_count"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting || !isDirty} loading={isSubmitting}>
              Search
            </Button>
            <Button type="button" color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default Form;
